import classNames from "classnames";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { Stepper } from "components/DesignSystem/Stepper/Stepper";
import React, { useState } from "react";
import { ModalProps } from "types/utils/modal";
import { ParsedListForBulkCart } from "types/Models/serviceTeam";
import { UploadCsvForBulkCartAction } from "./UploadCsvForBulkCartAction";
import {
  useAddBulkTaskMutation,
  useParseBulkCartActionCSVMutation,
} from "store/apis/serviceTeam";
import { useToast } from "hooks/useToast";
import { BackendError } from "types/utils/error";
import {
  AddDetailsForBulkCartAction,
  BulkCartActionFormValues,
  createInitialRow,
} from "./AddDetailsForBulkCartAction";
import { FormikForm } from "components/FormikForm/FormikForm";
import { bulkCartActionSchema } from "formValidations/bulkCartActionSchema";
import { ConfirmBulkTaskAdd } from "./ConfirmBulkTaskAdd";
import { useModal } from "hooks/useModal";
import { ArrowRight } from "components/icons/ArrowRight";

const steps = [
  {
    step: 1,
    name: "Upload CSV",
  },
  {
    step: 2,
    name: "Add Details",
  },
];

export const BulkCartActionModal = ({ isOpen, close }: ModalProps) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<ParsedListForBulkCart[] | null>(
    null
  );
  const [pageNum, setPageNum] = useState(1);
  const confirmBulkTaskAdd = useModal();

  const [validateCSV, { data, isLoading: isParsing }] =
    useParseBulkCartActionCSVMutation();
  const { alertToast, successToast } = useToast();
  const [addBulkTask, { isLoading: isAdding }] = useAddBulkTaskMutation();

  const handleCsvUpload = async (file: File, page: number = 1) => {
    try {
      setCsvFile(file);
      const result = await validateCSV({
        file,
        pageNum: page,
      }).unwrap();
      setParsedData(result?.template_details || []);
      setPageNum(page);
    } catch (error) {
      setCsvFile(null);
      setParsedData(null);
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  const handleBulkCartSubmit = async (values: BulkCartActionFormValues) => {
    try {
      await addBulkTask({
        productItems: values.rows,
        file: csvFile,
      }).unwrap();
      confirmBulkTaskAdd.close();
      close();
      successToast({ message: "Carts updated successfully" });
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={close} modal={false}>
      <Modal.Content size="xl" useCustomOverlay>
        <FormikForm
          initialValues={{
            rows: [createInitialRow()],
          }}
          validationSchema={
            currentStep === 2 ? bulkCartActionSchema : undefined
          }
          onSubmit={() => {}}
          validateOnMount={false}
          validateOnChange={false}
          validateOnBlur={false}
        >
          {({ values, validateForm }) => {
            const handleProceedClick = async () => {
              const validationErrors = await validateForm();
              if (Object.keys(validationErrors).length === 0) {
                confirmBulkTaskAdd.open();
              }
            };

            return (
              <>
                <Modal.Header>
                  <div>
                    <Modal.Title>Take Bulk Action on Tasks</Modal.Title>
                    <Modal.Subtitle className="t-mt-2">
                      <Stepper size="small" direction="horizontal">
                        {steps.map(({ step, name }) => (
                          <Stepper.Step
                            step={step}
                            key={step}
                            isActive={currentStep >= step}
                            clickable={currentStep >= step}
                            onClick={() => setCurrentStep(step)}
                          >
                            <div
                              className={classNames(
                                "t-flex t-items-center t-text-body-sm",
                                {
                                  "t-text-text-30": currentStep < step,
                                  "t-text-purple": currentStep >= step,
                                }
                              )}
                            >
                              {name}
                              {step !== steps.length && (
                                <ArrowRight color="currentColor" />
                              )}
                            </div>
                          </Stepper.Step>
                        ))}
                      </Stepper>
                    </Modal.Subtitle>
                  </div>
                  <Modal.Close />
                </Modal.Header>
                <Modal.Body>
                  {currentStep === 1 && (
                    <UploadCsvForBulkCartAction
                      file={csvFile}
                      parsedData={parsedData}
                      isLoading={isParsing}
                      pageNum={pageNum}
                      onCsvUpload={handleCsvUpload}
                      perPage={data?.per_page || 20}
                      totalCount={data?.total_count || 0}
                      totalPages={data?.total_pages || 0}
                    />
                  )}
                  {currentStep === 2 && <AddDetailsForBulkCartAction />}
                </Modal.Body>
                <Modal.FooterButtonGroup>
                  <Modal.RawClose asChild>
                    <Button>Cancel</Button>
                  </Modal.RawClose>
                  {currentStep === 1 && (
                    <Button
                      onClick={() => setCurrentStep(2)}
                      customType="primary"
                      type="button"
                      disabled={
                        !data?.template_details ||
                        data?.template_details?.length === 0
                      }
                    >
                      Next
                    </Button>
                  )}
                  {currentStep === 2 && (
                    <Button
                      onClick={handleProceedClick}
                      customType="primary"
                      type="button"
                    >
                      Proceed
                    </Button>
                  )}
                </Modal.FooterButtonGroup>
                <ConfirmBulkTaskAdd
                  {...confirmBulkTaskAdd}
                  itemCount={data?.template_details?.length || 0}
                  taskCount={values.rows.length}
                  confirm={() => handleBulkCartSubmit(values)}
                  isLoading={isAdding}
                />
              </>
            );
          }}
        </FormikForm>
      </Modal.Content>
    </Modal.Root>
  );
};
